import type { ProximityTier } from "@homero/pricing";
import { PaymentIntent } from "@stripe/stripe-js";
import type { Notice } from "src/components/utils/PriceNotice";

export const setPage = (page: number) => {
  return {
    type: "set_page",
    payload: page,
  };
};

export const setBedrooms = (bedrooms: string) => {
  return {
    type: "set_bedrooms",
    payload: bedrooms,
  };
};

export const setBathrooms = (bathrooms: string) => {
  return {
    type: "set_bathrooms",
    payload: bathrooms,
  };
};

export const setTypes = (types: string) => {
  return {
    type: "set_types",
    payload: types,
  };
};

export const getTotalPrice = () => {
  return {
    type: "get_total_price",
  };
};

export const getAppointmentValue = () => {
  return {
    type: "get_appointment_value",
  };
};

// step 2

export const setFlexibleTime = () => {
  return {
    type: "set_flexible_time",
  };
};

export const setDate = (date) => {
  return {
    type: "set_date",
    payload: date,
  };
};

export const setTime = (time) => {
  return {
    type: "set_time",
    payload: time,
  };
};

/**
 * Record that the customer took Same-Day, in a window the server offered.
 *
 * `surchargePercent` comes from the availability response for that date, not from
 * anything this app knows. It is used to show a total; the server re-prices the
 * booking from its own config at checkout and never trusts this number.
 *
 * The window's id *and* its label are both kept, because the two are read by
 * different consumers and neither substitutes for the other. `arrival_window` on
 * the wire is the id — the server matches it against its own configured windows —
 * while everything a human reads (`clean_time`, the confirmation email, the order
 * summary) needs the label. Keeping only the id is what made the confirmation
 * email say "midday" instead of "12:00-3:00pm".
 */
export const setSameDay = (
  arrivalWindow: string,
  arrivalWindowLabel: string,
  surchargePercent: number
) => {
  return {
    type: "set_same_day",
    payload: { arrivalWindow, arrivalWindowLabel, surchargePercent },
  };
};

/**
 * Switch the date step into Same-Day, before any window has been chosen.
 *
 * Same-Day is a mode rather than an extra control on the ordinary step, because
 * the ordinary controls are wrong for today. The fixed-time grid offers 8:00am
 * through 6:00pm from a static list — ask for it at 3pm and it cheerfully offers
 * to arrive at 8 that morning. Only the server knows which windows are still
 * reachable, so on today its answer is the *only* thing that may be shown.
 *
 * The date is set here rather than left to the calendar: entering the mode is
 * itself the choice of today.
 */
export const enterSameDay = (serviceDate: string) => {
  return {
    type: "enter_same_day",
    payload: serviceDate,
  };
};

/**
 * Drop the chosen arrival window without leaving Same-Day.
 *
 * For the window closing under the customer rather than for them changing their
 * mind — see the reducer case.
 */
export const clearSameDaySelection = () => {
  return {
    type: "clear_same_day_selection",
  };
};

/**
 * Leave Same-Day for the ordinary date and time step.
 *
 * Clears the date as well as the choice, so the step's own "pick the next
 * available date" effect runs again. Leaving today selected would drop the
 * customer back on the calendar with a date the ordinary controls cannot
 * correctly offer times for — the exact state this mode exists to avoid.
 */
export const exitSameDay = () => {
  return {
    type: "exit_same_day",
  };
};

export const setOccurance = (occurance: string | number) => {
  return {
    type: "set_occurance",
    payload: occurance,
  };
};

export const setAddress = (address: string) => {
  return {
    type: "set_address",
    payload: address,
  };
};

/** The ZIP the address checker pinned. Empty when it could not pin one. */
export const setZip = (zip: string) => {
  return {
    type: "set_zip",
    payload: zip,
  };
};

/** Opens or closes the header chip's ZIP editor. */
export const setZipEditorOpen = (open: boolean) => {
  return {
    type: "set_zip_editor_open",
    payload: open,
  };
};

/** The area-pricing tier quoted for `key` (see `quoteKey`), or none. */
export const setProximity = (payload: {
  tier: ProximityTier;
  percent: number;
  token: string | null;
  key: string;
  /** Whether the answer for `key` is still on its way. */
  pending?: boolean;
}) => {
  return {
    type: "set_proximity",
    payload,
  };
};

/** Why the price moved at checkout, or null to clear the notice. */
export const setPriceNotice = (payload: Notice | null) => {
  return {
    type: "set_price_notice",
    payload,
  };
};

export const setAppartment = (appartment: string) => {
  return {
    type: "set_appartment",
    payload: appartment,
  };
};

export const setAddons = (addon: any) => {
  return {
    type: "set_addon",
    payload: addon,
  };
};

export const setGetInType = (type: string, text: string = "") => {
  return {
    type: "set_getintype",
    payload: {
      type,
      text,
    },
  };
};

export const setPets = (pets: string, text: string = "") => {
  return {
    type: "set_pets",
    payload: { type: pets, text },
  };
};

export const setInstructions = (instructions: string) => {
  return {
    type: "set_instructions",
    payload: instructions,
  };
};

export const setPromocode = (code: string, codeDiscount: number) => {
  return {
    type: "set_promocode",
    payload: {
      code,
      codeDiscount,
    },
  };
};

export const setTip = (tip: number) => {
  return {
    type: "set_tip",
    payload: tip,
  };
};

export const setPaymentIntent = (paymentIntent: PaymentIntent) => {
  return {
    type: "set_payment_intent",
    payload: paymentIntent,
  };
};
export const setTaskPaymentIntent = (taskPaymentIntent: PaymentIntent) => {
  return {
    type: "set_task_payment_intent",
    payload: taskPaymentIntent,
  };
};

export const setToken = (token: string, credits: number) => {
  return {
    type: "set_token",
    payload: { token, credits },
  };
};
